import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, GitMerge, Loader2, AlertTriangle, CheckCircle2, Layers } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { useAuth } from '../../contexts/AuthContext';
import Navbar from '../../components/Navbar';
import { toast } from 'sonner';

export default function AdminSpecialtyMerge() {
  const { api } = useAuth();
  const [clusters, setClusters] = useState([]);
  const [loading, setLoading] = useState(true);
  // Per-cluster state: { [clusterKey]: { targetId, sourceIds: Set } }
  const [selections, setSelections] = useState({});
  const [confirmCluster, setConfirmCluster] = useState(null); // cluster object pending confirmation
  const [dryRunPreview, setDryRunPreview] = useState(null);
  const [merging, setMerging] = useState(false);

  const fetchClusters = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/specialties/merge-candidates');
      setClusters(res.data.clusters || []);
      // Initialize selections: pre-pick the densest specialty as target
      const initial = {};
      (res.data.clusters || []).forEach((c) => {
        if (c.members.length > 0) {
          initial[c.key] = { targetId: c.members[0].id, sourceIds: new Set() };
        }
      });
      setSelections(initial);
    } catch {
      toast.error('Erro ao carregar candidatos a mesclagem');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchClusters();
  }, [fetchClusters]);

  const setTarget = (clusterKey, id) => {
    setSelections((prev) => {
      const current = prev[clusterKey] || { sourceIds: new Set() };
      const next = { ...current, targetId: id };
      // If target is now in sourceIds, drop it
      if (next.sourceIds.has(id)) {
        const newSet = new Set(next.sourceIds);
        newSet.delete(id);
        next.sourceIds = newSet;
      }
      return { ...prev, [clusterKey]: next };
    });
  };

  const toggleSource = (clusterKey, id) => {
    setSelections((prev) => {
      const current = prev[clusterKey] || { targetId: null, sourceIds: new Set() };
      if (id === current.targetId) return prev; // can't toggle target
      const newSet = new Set(current.sourceIds);
      if (newSet.has(id)) newSet.delete(id); else newSet.add(id);
      return { ...prev, [clusterKey]: { ...current, sourceIds: newSet } };
    });
  };

  const openConfirm = async (cluster) => {
    const sel = selections[cluster.key];
    if (!sel || !sel.targetId || sel.sourceIds.size === 0) {
      toast.error('Selecione ao menos uma origem para mesclar');
      return;
    }
    setMerging(true);
    setConfirmCluster(cluster);
    try {
      const res = await api.post('/admin/specialties/merge', {
        source_ids: Array.from(sel.sourceIds),
        target_id: sel.targetId,
        dry_run: true,
      });
      setDryRunPreview(res.data);
    } catch {
      toast.error('Erro ao calcular pré-visualização');
      setConfirmCluster(null);
    } finally {
      setMerging(false);
    }
  };

  const confirmMerge = async () => {
    if (!confirmCluster) return;
    const sel = selections[confirmCluster.key];
    setMerging(true);
    try {
      const res = await api.post('/admin/specialties/merge', {
        source_ids: Array.from(sel.sourceIds),
        target_id: sel.targetId,
        dry_run: false,
      });
      toast.success(`Mesclado: ${res.data.cnpjs_moved} CNPJs movidos para ${res.data.target.name}`);
      setConfirmCluster(null);
      setDryRunPreview(null);
      fetchClusters();
    } catch {
      toast.error('Erro ao executar mesclagem');
    } finally {
      setMerging(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-specialty-merge">
      <Navbar />
      <div className="container-app py-8">
        <Link to="/admin" className="inline-flex items-center gap-2 text-slate-600 hover:text-primary mb-6" data-testid="merge-back-link">
          <ChevronLeft className="w-4 h-4" />
          Voltar
        </Link>

        <div className="flex items-start justify-between mb-6 gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-2">
              <GitMerge className="w-7 h-7 text-primary" />
              Mesclar Especialidades
            </h1>
            <p className="text-slate-600 mt-1 max-w-2xl">
              Identifique especialidades sinônimas (ex: <em>Fisioterapia</em>, <em>Clínica de Fisioterapia</em>, <em>Fisioterapeuta</em>) e mescle todas em uma única canônica.
              Os CNPJs migram automaticamente; as fontes ficam desativadas.
            </p>
          </div>
          <div className="text-right text-sm text-slate-500 shrink-0">
            <Layers className="w-4 h-4 inline mr-1" />
            {!loading && `${clusters.length} cluster(s) detectado(s)`}
          </div>
        </div>

        {loading ? (
          <div className="grid gap-4">
            {[...Array(3)].map((_, i) => (
              <div key={`skel-${i}`} className="animate-pulse h-40 bg-white rounded-2xl" />
            ))}
          </div>
        ) : clusters.length === 0 ? (
          <div className="bg-white rounded-2xl border border-emerald-200 p-12 text-center">
            <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
            <p className="text-lg font-medium text-slate-900">Nenhum cluster de sinônimos detectado</p>
            <p className="text-sm text-slate-500 mt-2">Suas especialidades já estão consolidadas.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {clusters.map((cluster) => {
              const sel = selections[cluster.key] || { targetId: null, sourceIds: new Set() };
              return (
                <div
                  key={cluster.key}
                  className="bg-white rounded-2xl border border-slate-100 p-5"
                  data-testid={`cluster-${cluster.key}`}
                >
                  <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-100">
                    <div>
                      <p className="text-xs uppercase tracking-wide text-slate-400">Cluster</p>
                      <p className="font-semibold text-slate-900">
                        Raiz <span className="font-mono text-primary">{cluster.key}</span>
                        <span className="ml-2 text-sm font-normal text-slate-500">
                          ({cluster.members.length} especialidades · {cluster.total_cnpjs} CNPJs no total)
                        </span>
                      </p>
                    </div>
                    <Button
                      onClick={() => openConfirm(cluster)}
                      disabled={!sel.targetId || sel.sourceIds.size === 0 || merging}
                      className="bg-primary"
                      data-testid={`merge-btn-${cluster.key}`}
                    >
                      <GitMerge className="w-4 h-4 mr-2" />
                      Mesclar selecionadas ({sel.sourceIds.size})
                    </Button>
                  </div>

                  <div className="grid grid-cols-12 gap-2 text-xs uppercase tracking-wide text-slate-400 px-3 mb-2">
                    <div className="col-span-6">Especialidade</div>
                    <div className="col-span-2 text-right">CNPJs</div>
                    <div className="col-span-2 text-center">Manter (alvo)</div>
                    <div className="col-span-2 text-center">Mesclar (origem)</div>
                  </div>

                  <div className="space-y-1">
                    {cluster.members.map((m) => {
                      const isTarget = sel.targetId === m.id;
                      const isSource = sel.sourceIds.has(m.id);
                      return (
                        <div
                          key={m.id}
                          className={`grid grid-cols-12 gap-2 items-center px-3 py-2 rounded-lg ${
                            isTarget ? 'bg-emerald-50' : isSource ? 'bg-amber-50' : 'hover:bg-slate-50'
                          }`}
                        >
                          <div className="col-span-6 font-medium text-slate-800 truncate" title={m.name}>
                            {m.name}
                          </div>
                          <div className="col-span-2 text-right tabular-nums text-slate-600">{m.cnpj_count}</div>
                          <div className="col-span-2 text-center">
                            <input
                              type="radio"
                              name={`target-${cluster.key}`}
                              checked={isTarget}
                              onChange={() => setTarget(cluster.key, m.id)}
                              className="w-4 h-4 accent-emerald-600 cursor-pointer"
                              data-testid={`target-radio-${m.id}`}
                            />
                          </div>
                          <div className="col-span-2 text-center">
                            <input
                              type="checkbox"
                              checked={isSource}
                              disabled={isTarget}
                              onChange={() => toggleSource(cluster.key, m.id)}
                              className="w-4 h-4 accent-amber-600 cursor-pointer disabled:opacity-30"
                              data-testid={`source-checkbox-${m.id}`}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={!!confirmCluster} onOpenChange={(open) => { if (!open) { setConfirmCluster(null); setDryRunPreview(null); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Confirmar mesclagem
            </DialogTitle>
            <DialogDescription>
              Esta ação atualiza todos os CNPJs e desativa as especialidades de origem. Não há rollback automático.
            </DialogDescription>
          </DialogHeader>

          {dryRunPreview ? (
            <div className="space-y-3 py-2">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                <p className="text-xs uppercase text-emerald-700 mb-1">Manter como canônica</p>
                <p className="font-semibold text-emerald-900">{dryRunPreview.target.name}</p>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-xs uppercase text-amber-700 mb-1">Mesclar e desativar ({dryRunPreview.sources_to_deactivate})</p>
                <ul className="text-sm text-amber-900 space-y-0.5">
                  {dryRunPreview.sources.map((s) => (
                    <li key={s.id}>• {s.name}</li>
                  ))}
                </ul>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 flex items-center justify-between">
                <span className="text-sm text-slate-700">CNPJs que serão movidos</span>
                <span className="text-2xl font-bold text-slate-900 tabular-nums">{dryRunPreview.cnpjs_to_move}</span>
              </div>
            </div>
          ) : (
            <div className="py-8 text-center">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400 mx-auto" />
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { setConfirmCluster(null); setDryRunPreview(null); }} disabled={merging}>
              Cancelar
            </Button>
            <Button
              onClick={confirmMerge}
              disabled={merging || !dryRunPreview}
              className="bg-primary"
              data-testid="confirm-merge-btn"
            >
              {merging ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <GitMerge className="w-4 h-4 mr-2" />}
              Confirmar mesclagem
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
